import React, { useCallback, useContext, useMemo } from "react";
import styles from "./styles.module.scss";
import config from "config/environment";
import { SidebarProps } from "../index.types";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { hasFeature } from "app/utils/hasFeature";
import {
  isFacilityStaff,
  showForNonFacilityAdminStaff,
  showForSchedulingAdminsOrCoaches,
} from "app/utils/roleAccess";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";

import { UserModel } from "app/models";
import Item from "./Item";
import { some } from "lodash";
import {
  UserContext,
  UserContextType,
  RoleType,
} from "app/contexts/UserContext";

export type MenuItemData = {
  title: string;
  icon?: string;
  route: string;
  targetBlank: boolean;
  displayIf?: (user: UserModel, self: MenuItemData) => boolean;
  activeMatchPaths?: string[];
  displayChildrenIf?: (user: UserModel, self: MenuItemData) => boolean;
  children?: MenuItemData[];
};

const facilityHasCoaches = (user: UserContextType): boolean => {
  if (!user.facility) {
    return false;
  }

  if (!user.facility.coachProfiles) {
    return false;
  }

  return user.facility.coachProfiles.length > 0;
};

const facilityHasStripeFlag = (user: UserContextType): boolean => {
  return user.facility?.hasFacilityLevelStripe === true;
};

const showForCoach = (user: UserContextType, _self: MenuItemData): boolean => {
  return user.kind === "coach" && (!user.role || user.role === RoleType.COACH);
};

const showForSchedulingCoach = (
  user: UserContextType,
  _self: MenuItemData,
): boolean => {
  return (
    user.kind === "coach" &&
    (user.role === RoleType.COACH || !user.facilityMembershipId)
  );
};

const showForSchedulingStaff = (
  user: UserContextType,
  _self: MenuItemData,
): boolean => {
  return (
    (user.role === RoleType.ADMIN || user.role === RoleType.SCHEDULER) &&
    facilityHasCoaches(user)
  );
};

// Coaches and schedulers read the facility Resources page. Admins and Coach
// Admins open it from the Admin Settings tabs.
export const showFacilityResources = (
  user: UserContextType,
  _self: MenuItemData,
): boolean => {
  return (
    isFacilityStaff(user) &&
    !user.behavesLikeAdmin &&
    user.facility?.featureFlags?.resourceManagement === true
  );
};

const showForAthlete = (
  user: UserContextType,
  _self: MenuItemData,
): boolean => {
  return user.kind === "athlete" || user.kind === "other";
};

const showForSchedulingUsers = (
  user: UserContextType,
  _self: MenuItemData,
): boolean => {
  if (user.role === RoleType.ADMIN || user.role === RoleType.SCHEDULER) {
    return facilityHasCoaches(user);
  }
  return hasFeature(user.features, "scheduling");
};

const showForSchedulingCoaches = (
  user: UserContextType,
  self: MenuItemData,
): boolean => {
  return (
    hasFeature(user.features, "scheduling") &&
    showForSchedulingCoach(user, self)
  );
};

const showCoachBillingWithoutFacilityFlag = (
  user: UserContextType,
  self: MenuItemData
): boolean => {
  // If user belongs to a facility but facility data hasn't loaded yet,
  // hide billing to prevent flash before we know the facility's stripe status
  if (user.facilityMembershipId && !user.facility) {
    return false;
  }
  // Hide coach billing if their facility has the stripe integration flag
  if (facilityHasStripeFlag(user)) {
    return false;
  }
  return showForSchedulingCoaches(user, self);
};

const showForSchedulingAthletes = (
  user: UserContextType,
  self: MenuItemData,
): boolean => {
  return hasFeature(user.features, "scheduling") && showForAthlete(user, self);
};

const showMyFaciltyOnTop = (
  user: UserContextType,
  _self: MenuItemData,
): boolean => {
  return user.role === RoleType.ADMIN;
};

const showForAdmin = (user: UserContextType, _self: MenuItemData): boolean => {
  return user.role === RoleType.ADMIN;
};


const showFacilityBilling = (
  user: UserContextType,
  _self: MenuItemData
): boolean => {
  // Show for admins and coach admins when facility has stripe flag
  const isAdminOrCoachAdmin = user.role === RoleType.ADMIN || user.behavesLikeAdmin;
  return isAdminOrCoachAdmin && facilityHasStripeFlag(user);
};

const showMyFaciltyAtBottom = (
  user: UserContextType,
  _self: MenuItemData,
): boolean => {
  return user.role === RoleType.COACH && user.isAdmin;
};

const hideOnlyForCoaches = (
  user: UserContextType,
  _self: MenuItemData,
): boolean => {
  return user.kind !== "coach";
};

const SidebarMenuList = (_props: SidebarProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { self } = useSelector((state: RootState) => state.user);
  // Directly call hasFeature here instead of using useCallback inside useMemo
  const hasSchedulingFeature = hasFeature(self?.features, "scheduling");
  const user = useContext(UserContext);

  const listData = useMemo<MenuItemData[]>(
    () => [
      {
        title: t("Home"),
        icon: "ico-home-o",
        route: "/home",
        targetBlank: false,
        // forAthlete: false,
        displayIf: hideOnlyForCoaches,
      },
      {
        title: t("Home"),
        icon: "ico-home-o",
        route: "/contacts",
        activeMatchPaths: ["contacts"],
        targetBlank: false,
        displayIf: showForAdmin,
      },
      {
        title: t("Home"),
        icon: "ico-home-o",
        route: "/spaces",
        activeMatchPaths: ["spaces", "groups", "lists", "contacts"],
        targetBlank: false,
        // forAthlete: true,
        displayIf: showForCoach,
      },
      {
        title: t("Feed"),
        icon: "ico-feed-o",
        route: "/feed",
        targetBlank: false,
        // forAthlete: true,
        displayIf: showForNonFacilityAdminStaff,
      },
      {
        title: t("Activity"),
        icon: "ico-notifications-o",
        route: "/activity/notifications",
        activeMatchPaths: ["booking_logs", "invitations", "notifications"],
        targetBlank: false,
        displayIf: showForNonFacilityAdminStaff,
      },
      {
        title: t("Calendar"),
        icon: "ico-calendar",
        route: "/calendar",
        activeMatchPaths: ["calendar"],
        targetBlank: false,
        displayIf: showForSchedulingCoaches,
      },
      {
        title: t("Calendar"),
        route: "/calendar/facility",
        activeMatchPaths: ["calendar"],
        icon: "ico-calendar",
        targetBlank: false,
        displayIf: showForSchedulingStaff,
      },
      {
        title: t("Resources"),
        icon: "ico-facility",
        route: "/facility_resources",
        targetBlank: false,
        displayIf: showFacilityResources,
      },
      {
        title: t("Bookings"),
        icon: "ico-calendar",
        route: "/sessions",
        targetBlank: false,
        displayIf: showForSchedulingAthletes,
      },
      {
        title: t("Billing"),
        icon: "ico-dollar-circle",
        route: "/billing",
        targetBlank: false,
        displayIf: showCoachBillingWithoutFacilityFlag,
      },
      {
        title: t("Programs"),
        icon: "ico-program",
        route: "/programs",
        targetBlank: false,
        displayIf: showForSchedulingUsers,
      },
      {
        title: t("Library"),
        icon: "ico-library-o",
        route: "/resources",
        targetBlank: false,
        displayIf: showForNonFacilityAdminStaff,
      },
      {
        title: t("Templates"),
        icon: "ico-templates",
        route: "/space_templates",
        targetBlank: false,
        // forAthlete: false,
        displayIf: showForCoach,
      },
      {
        title: t("Schedule"),
        icon: "ico-time",
        route: "/schedule",
        targetBlank: false,
        // forAthlete: false,
        displayIf: showForCoach,
      },
      {
        title: t("Automation"),
        icon: "ico-time",
        route: "/automation/facility-workflows",
        targetBlank: false,
        // forAthlete: false,
        displayIf: (user: UserContextType) =>
          !!user.facilityPermissions?.includes("access_workflows"),
      },

      {
        title: t("Reports"),
        route: "/reports",
        icon: "ico-analytics",
        targetBlank: false,
        displayIf: showForSchedulingAdminsOrCoaches,
      },
      {
        title: t("Billing"),
        icon: "ico-dollar-circle",
        route: "/facility_billing",
        targetBlank: false,
        displayIf: showFacilityBilling,
      },
      {
        title: t("Admin Settings"),
        route: "/manage_facility",
        icon: "ico-cog",
        targetBlank: false,
        displayIf: showMyFaciltyOnTop,
      },
      {
        title: t("Admin Settings"),
        route: "/manage_facility",
        icon: "ico-cog",
        targetBlank: false,
        displayIf: showMyFaciltyAtBottom,
      },
      {
        title: t("Support & FAQ"),
        icon: "ico-heart",
        route: config.SUPPORT_URL,
        targetBlank: true,
      },
    ],
    [hasSchedulingFeature, t],
  );

  const selectHandler = useCallback((val: string, isTargetBlank?: boolean) => {
    if (!isTargetBlank) {
      history.push(val);
    } else {
      window.open(val);
    }
  }, []);

  const checkActivePath = (route: string, activeMatchPaths?: string[]) => {
    const currentPath = location.pathname;

    if (activeMatchPaths && activeMatchPaths.length > 0) {
      return activeMatchPaths.some((match) => {
        const normalized = match.startsWith("/") ? match : `/${match}`;
        return (
          currentPath.includes(normalized) ||
          currentPath.includes(`/public${normalized}`)
        );
      });
    }

    if (route === "/") {
      return currentPath === "/";
    }

    return (
      currentPath.startsWith(route) || currentPath.startsWith(`/public${route}`)
    );
  };

  const checkActive = (menuItem: MenuItemData): boolean => {
    if (menuItem.children?.length) {
      return some(menuItem.children, (child) =>
        checkActivePath(child.route, child?.activeMatchPaths),
      );
    }

    return checkActivePath(menuItem.route, menuItem?.activeMatchPaths);
  };

  const showMenuItemChildren = (menuItem: MenuItemData) => {
    if (!menuItem.children?.length) {
      return false;
    }

    if (!menuItem.displayChildrenIf) {
      return true;
    }

    return menuItem.displayChildrenIf(user, menuItem);
  };

  const generateTestId = (route: string, isChild = false): string => {
    const cleanRoute = route.replace(/^\//, '').replace(/\//g, '-') || 'home';
    return isChild ? `sidebar-nav-child-${cleanRoute}` : `sidebar-nav-${cleanRoute}`;
  };

  return (
    <div className={styles.sidebar_nav}>
      {listData.map((list, index) => {
        const isActive = checkActive(list);
        const hasChildren = showMenuItemChildren(list);

        return (
          <Item
            key={`${list.route}-${index}`}
            data={list}
            user={user}
            isActive={isActive}
            onClick={selectHandler}
            activeChildren={hasChildren}
            data-testid={generateTestId(list.route)}
          >
            {hasChildren && (
              <div className={styles.child_nav_item}>
                {list.children.map((childList: MenuItemData) => (
                  <Item
                    key={`child-${childList.route}`}
                    data={childList}
                    user={user}
                    isActive={checkActivePath(
                      childList.route,
                      childList?.activeMatchPaths,
                    )}
                    onClick={selectHandler}
                    data-testid={generateTestId(childList.route, true)}
                  />
                ))}
              </div>
            )}
          </Item>
        );
      })}
    </div>
  );
};

export default SidebarMenuList;
